<template>
	<div
		ref="datatable"
		class="new-datatable"
		:class="{
			checkboxed: checkbox,
		}"
		:style="{
			maxHeight: maxHeight || null,
		}"
		@scroll.passive="ScrollEvent"
	>
		<div
			class="datatable-header px-4"
			:style="gridStyle"
		>
			<div
				v-if="checkbox"
				class="datatable-cell checkbox-cell"
			>
				<div class="form-check">
					<input
						id="datatable-select-all"
						ref="selectAll"
						type="checkbox"
						:checked="allSelected"
						hidden
						@change="toggleAll"
					/>
					<label
						for="datatable-select-all"
						class="form-check-label"
						:class="{
							partial: someSelected && !allSelected,
							full: allSelected,
						}"
						@click.stop.prevent="toggleAllFromLabel"
					></label>
				</div>
			</div>
			<template v-if="!omitHeaders">
				<div
					v-for="(header, index) in headers"
					:key="header.value || index"
					class="datatable-cell"
				>
					{{ header.text }}
				</div>
			</template>
		</div>

		<div class="datatable-body px-4">
			<div
				v-if="filteredValues.length === 0"
				class="w-full flex-1 flex justify-center items-center"
			>
				<p class="mb-0 text-center text-opacity-25">
					{{ emptyText }}
				</p>
			</div>
			<div
				v-for="(item, index) in filteredValues"
				v-else
				:key="getItemKey(item, index)"
				class="datatable-row"
				:class="{
					'is-selected': isSelected(item, index),
					'cursor-pointer': $listeners.click,
				}"
				:style="getRowStyle(item, index)"
				@click="$emit('click', item)"
				@dblclick="$emit('dblclick', item)"
			>
				<div
					v-if="checkbox"
					class="datatable-cell checkbox-cell"
				>
					<div class="form-check">
						<input
							:id="getCheckboxId(item, index)"
							type="checkbox"
							:checked="isSelected(item, index)"
							hidden
							@change="toggleItem(item, index)"
						/>
						<label
							:for="getCheckboxId(item, index)"
							class="form-check-label"
							@click.stop.prevent="toggleItem(item, index)"
						></label>
					</div>
				</div>
				<slot
					name="row"
					:item="item"
					:index="index"
					:selected="isSelected(item, index)"
				/>
			</div>
		</div>
	</div>
</template>

<script>
	export default {
		name: 'NewDatatable',
		model: {
			prop: 'value',
			event: 'input',
		},
		props: {
			headers: {
				type: Array,
				default: () => [],
			},
			value: {
				type: Array,
				default: () => [],
			},
			checkbox: {
				type: Boolean,
				default: true,
			},
			selecteds: {
				type: Array,
				default: () => [],
			},
			rowColors: {
				type: Object,
				default: () => ({}),
			},
			rowTypeKey: {
				type: String,
				default: 'status',
			},
			itemKey: {
				type: String,
				default: 'id',
			},
			emptyText: {
				type: String,
				default: 'NÃO HÁ REGISTROS PARA EXIBIR',
			},
			lazyLoading: {
				type: Boolean,
				default: false,
			},
			omitHeaders: {
				type: Boolean,
				default: false,
			},
			maxHeight: {
				type: String,
				default: '',
			},
			search: {
				type: String,
				default: '',
			},
			searchColumn: {
				type: String,
				default: '',
			},
		},
		data() {
			return {
				selectedKeys: [],
				reachedBottom: false,
				searchTimeout: null,
			}
		},
		computed: {
			selectedModel: {
				get() {
					return this.selecteds
				},
				set(value) {
					this.$emit('update:selecteds', value)
				},
			},
			gridStyle() {
				let columns = []

				if (this.checkbox) {
					columns.push('40px')
				}

				columns = columns
					.concat(
						this.headers.map(header => {
							if (header && header.width) {
								return header.width
							}
							return '1fr'
						})
					)
					.join(' ')

				return {
					display: 'grid',
					gridTemplateColumns: columns,
				}
			},
			allSelected() {
				return this.value.length > 0 && this.selectedKeys.length === this.value.length
			},
			someSelected() {
				return this.selectedKeys.length > 0 && this.selectedKeys.length < this.value.length
			},
			filteredValues() {
				if (this.searchTimeout) clearTimeout(this.searchTimeout)

				if (this.search === '' || this.searchColumn === '') return this.value

				let filteredValues = []

				filteredValues = this.value.filter(item => {
					const search = window.helpers
						.RemoveAcento(this.search.toLowerCase())
						.toString()
						.toLowerCase()
						.trim()
					const itemValue = window.helpers
						.RemoveAcento(item[this.searchColumn].toString().toLowerCase())
						.toString()
						.toLowerCase()
						.trim()
					return itemValue.includes(search)
				})

				return filteredValues
			},
		},
		watch: {
			value: {
				handler() {
					this.syncSelection()
					this.reachedBottom = false
				},
				deep: true,
				immediate: true,
			},
			selecteds: {
				handler() {
					this.syncSelection()
				},
				deep: true,
				immediate: true,
			},
			someSelected() {
				this.updateIndeterminate()
			},
			allSelected() {
				this.updateIndeterminate()
			},
		},
		mounted() {
			this.selectedKeys = Array.isArray(this.selecteds) ? this.selecteds.slice() : []
			this.updateIndeterminate()
		},
		methods: {
			getCheckboxId(item, index) {
				const key = this.getItemKey(item, index)
				return `datatable-checkbox-${String(key)}`
			},
			getItemKey(item, index) {
				let key = this.itemKey

				if (item && item[key]) {
					return item[key]
				}

				return index
			},
			getRowStyle(item, index) {
				const style = { ...this.gridStyle }
				if (!item || !this.rowTypeKey || !this.rowColors) {
					return style
				}
				const typeValue = item[this.rowTypeKey]
				const color = this.rowColors[typeValue]
				if (!color) {
					return style
				}

				style['--row-accent-color'] = color
				return style
			},
			isSelected(item, index) {
				const key = this.getItemKey(item, index)
				return this.selectedKeys.includes(key)
			},
			toggleItem(item, index) {
				const key = this.getItemKey(item, index)
				if (this.selectedKeys.includes(key)) {
					this.selectedKeys = this.selectedKeys.filter(selected => selected !== key)
					this.selectedModel = this.selectedKeys
					return
				}
				this.selectedKeys = this.selectedKeys.concat(key)
				this.selectedModel = this.selectedKeys
			},
			toggleAllFromLabel() {
				this.setAll(!this.allSelected)
			},
			toggleAll(event) {
				this.setAll(event.target.checked)
			},
			setAll(checked) {
				if (!checked) {
					this.selectedKeys = []
					this.selectedModel = []
					return
				}
				this.selectedKeys = this.value.map((item, index) => this.getItemKey(item, index))
				this.selectedModel = this.selectedKeys
			},
			syncSelection() {
				const keys = this.value.map((item, index) => this.getItemKey(item, index))
				const selecteds = Array.isArray(this.selecteds) ? this.selecteds : []
				this.selectedKeys = selecteds.filter(key => keys.includes(key))
				if (this.selectedKeys.length !== selecteds.length) {
					this.selectedModel = this.selectedKeys
				}
			},
			updateIndeterminate() {
				if (this.$refs.selectAll) {
					this.$refs.selectAll.indeterminate = this.someSelected && !this.allSelected
				}
			},
			ScrollEvent() {
				if (!this.lazyLoading) return

				const table = this.$refs.datatable
				if (!table) return

				const bottomTable = table.offsetHeight + Math.ceil(table.scrollTop) >= table.scrollHeight

				if (!bottomTable) {
					this.reachedBottom = false
					return
				}

				if (this.reachedBottom) return

				this.reachedBottom = true
				this.$emit('load-table')
			},
		},
	}
</script>

<style lang="scss" scoped>
	.new-datatable {
		width: 100%;
		min-height: 100%;
		max-height: 38.7rem;
		display: flex;
		flex-direction: column;
		align-items: stretch;
		overflow-y: auto;
		scrollbar-gutter: stable;
		color: var(--fontColor);

		.datatable-header {
			position: sticky;
			top: 0;
			z-index: 2;
			background: var(--elementsBackgroundColor);
			border-bottom: 1px solid var(--elementsBorderColor);

			.datatable-cell {
				font-weight: 900;
				color: var(--fontColor);
			}
		}

		.datatable-body {
			flex-grow: 1;
			min-height: 0;
			display: flex;
			flex-direction: column;
		}

		.datatable-row,
		.datatable-header {
			align-items: start;
		}

		.datatable-row {
			position: relative;
			margin: 0;
			background: transparent;
			align-items: center;

			&::before {
				content: '';
				position: absolute;
				left: 0;
				right: 0;
				top: 0;
				bottom: 0;
				border: 1px solid transparent;
				background: transparent;
				pointer-events: none;
				transition: border-color 0.15s ease;
			}

			&:hover::before {
				border-color: var(--elementsHoverBorderColor);
			}
			&.is-selected::before {
				border-color: var(--elementsSelectedBorderColor);
			}

			&:hover .datatable-cell:not(.checkbox-cell),
			&.is-selected .datatable-cell:not(.checkbox-cell) {
				color: var(--row-accent-color, inherit);
			}
		}

		.datatable-cell {
			padding: 0.625rem 0.75rem;
			text-align: center;
			display: flex;
			align-items: center;
			justify-content: flex-start;
			min-height: 2.75rem;
			position: relative;
			z-index: 1;

			&.doc_id {
				gap: 0.3rem;
			}
		}

		.checkbox-cell {
			justify-content: center;
			padding: 0.625rem 0.75rem 0.625rem 0;
		}

		.datatable-cell.actions {
			padding-top: 0;
			padding-bottom: 0;
		}

		&.checkboxed {
			.datatable-body {
				.datatable-row {
					&::before {
						left: 2.5rem;
					}
				}
			}
		}
	}
</style>
